import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {handleRepositoryDataError} from '@github-ui/relay-error-helpers'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {RepositoryMilestoneInternal} from '@github-ui/repository-milestone'
import {useMilestoneHeaderActions} from '@github-ui/repository-milestone/use-milestone-header-actions'
import {PageHeader} from '@primer/react'
import {useEffect} from 'react'
import {
  type EntryPointComponent,
  graphql,
  type PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
} from 'react-relay'

import {renderIssuesSidebar} from '../components/sidebar/render-issues-sidebar'
import {useSetCurrentViewId} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import type {RepositoryMilestonePageContentInternal$key} from './__generated__/RepositoryMilestonePageContentInternal.graphql'
import type {
  RepositoryMilestonePageQuery,
  RepositoryMilestonePageQuery$variables,
} from './__generated__/RepositoryMilestonePageQuery.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query RepositoryMilestonePageQuery($name: String!, $owner: String!, $number: Int!, $first: Int!, $query: String!) {
    repository(owner: $owner, name: $name) @catch(to: RESULT) {
      id @required(action: THROW)
      ...RepositoryMilestonePageContentInternal @arguments(first: $first, number: $number, query: $query)
    }
  }
`

export const RepositoryMilestonePage: EntryPointComponent<
  {pageQuery: RepositoryMilestonePageQuery},
  Record<string, never>
> = ({queries: {pageQuery}}) => {
  const {queryRef, loadQuery} = useEntryPointsLoader<RepositoryMilestonePageQuery>(pageQuery, PageQuery)
  if (!queryRef) return null

  return (
    <AnalyticsWrapper category="Repository Milestone Show">
      <RepositoryMilestoneContent pageQueryRef={queryRef} loadQuery={loadQuery} />
    </AnalyticsWrapper>
  )
}

function RepositoryMilestoneContent({
  pageQueryRef,
}: {
  pageQueryRef: PreloadedQuery<RepositoryMilestonePageQuery>
  loadQuery: (
    variables: RepositoryMilestonePageQuery$variables,
    options?: UseQueryLoaderLoadQueryOptions | undefined,
  ) => void
}) {
  const pageData = usePreloadedQuery<RepositoryMilestonePageQuery>(PageQuery, pageQueryRef)

  const setCurrentViewId = useSetCurrentViewId()
  useEffect(() => {
    setCurrentViewId(VIEW_IDS.repository)
  }, [pageQueryRef, setCurrentViewId])

  const repositoryData = pageData?.repository

  const okResult = handleRepositoryDataError(repositoryData)
  if (!okResult || !okResult.value) {
    return null
  }

  return <RepositoryMilestonePageContentInternal repository={okResult.value} />
}

function RepositoryMilestonePageContentInternal({
  repository,
}: {
  repository: RepositoryMilestonePageContentInternal$key
}) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment RepositoryMilestonePageContentInternal on Repository
      @argumentDefinitions(first: {type: "Int!"}, number: {type: "Int!"}, query: {type: "String!"}) {
        nameWithOwner
        viewerCanPush
        name
        owner {
          login
        }
        milestone(number: $number) {
          # eslint-disable-next-line relay/must-colocate-fragment-spreads
          ...ToggleMilestoneState
          title
          number
        }
        # eslint-disable-next-line relay/must-colocate-fragment-spreads
        ...RepositoryMilestoneInternal @arguments(first: $first, number: $number, query: $query)
      }
    `,
    repository,
  )

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()
  const milestone = data.milestone
  const liftHeader = sidebarLayoutEnabled && milestone != null

  const headerTitle = liftHeader ? milestone.title : undefined
  const contextArea = liftHeader ? (
    <PageHeader.ParentLink hidden={false} href={`/${data.nameWithOwner}/milestones`}>
      Back to milestones
    </PageHeader.ParentLink>
  ) : undefined
  const headerActions = liftHeader ? (
    <MilestoneHeaderActions
      viewerCanPush={data.viewerCanPush}
      milestoneRef={milestone}
      milestoneNumber={milestone.number}
      milestoneTitle={milestone.title}
      ownerLogin={data.owner.login}
      repoName={data.name}
    />
  ) : undefined

  return (
    <RepoIssuesPageLayout
      dialogTitle="Milestones"
      title={headerTitle}
      contextArea={contextArea}
      actions={headerActions}
      renderSidebar={renderIssuesSidebar}
    >
      <RepositoryMilestoneInternal repository={data} />
    </RepoIssuesPageLayout>
  )
}

type MilestoneHeaderActionsProps = Parameters<typeof useMilestoneHeaderActions>[0]

function MilestoneHeaderActions(props: MilestoneHeaderActionsProps) {
  const actions = useMilestoneHeaderActions(props)
  return <FilteredListPageLayout.HeaderActions menuAriaLabel="More milestone actions" actions={actions} />
}
